import { useQueryParams } from '../../hooks/misc';
import { useApolloContext } from '../../graphql/ApiProvider';
import { loader } from 'graphql.macro';
import { useQuery, useMutation } from '@apollo/client';
import { useState, useEffect, useMemo } from 'react';
import styles from './AddObservationNoAuth.module.scss';
import { Text } from '../../components/typography';
import { keys } from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components';
import { DropdownText } from '../../components/dropdowns/DropdownText';
import { Form } from 'react-bootstrap';
import { inputTypes } from '../../constants/strings';
import { dateOptions } from '../../utilities/inspection';
import MultiSelect from '../../components/forms/MultiSelect';
import moment from 'moment';
import logo from '../../assets/logo_white_horiz.svg';
import background from '../../assets/logo_white_icon.svg';
import blueLogo from '../../assets/logo_grad.svg';
import { showToast, toastVar, hideToast } from '../../graphql/cache/modal';
import { toastVariant } from '../../constants/misc';
import { useReactiveVar } from '@apollo/client';
import Toast from '../../components/modals/Toast';
import CustomSpinner from '../../components/CustomSpinner';
import DeletedReportPage from '../DeletedReportPage';

const noAuthContextQuery = loader('./AddObservationNoAuth.fetch.graphql');
const submiteObservationMutation = loader(
  './AddObservationNoAuth.submit.graphql',
);

export default function AddObservationNoAuth() {
  const toastProps = useReactiveVar(toastVar);
  const { t } = useTranslation();
  const { accessKey, setAccessKey } = useApolloContext();
  const { getParam } = useQueryParams();
  const key = getParam('accessKey');
  const workspaceId = getParam('workspaceId');
  const [reporter, setReporter] = useState(null);
  const [observationState, setObservationState] = useState({});
  const [submitObservation] = useMutation(submiteObservationMutation);
  const [submitted, setSubmitted] = useState(false);

  const { negative, description, dateTime, location, questions, answers } =
    useMemo(() => observationState || {}, [observationState]);

  const update = (variables) =>
    setObservationState({ ...observationState, ...variables });

  const handleInputChange = (id, value, title, description) => {
    const updatedQuestions = questions?.map((q) =>
      q.id === id ? { ...q, value, title, description } : q,
    );
    update({
      answers: updatedQuestions,
    });
  };

  const { loading, data: { workspaces = [] } = {} } = useQuery(
    noAuthContextQuery,
    {
      skip: !accessKey && !workspaceId,
      variables: {
        workspaceId,
      },
    },
  );

  const workspace = useMemo(
    () => (workspaces ? workspaces[0] : null),
    [workspaces],
  );

  useEffect(
    () => (key ? setAccessKey(key) : setAccessKey('')),
    [key, setAccessKey],
  );

  useEffect(
    () =>
      workspace
        ? setObservationState((o) => ({
            ...o,
            questions: workspace?.observationTemplate?.categories[0]?.questions,
          }))
        : {},
    [workspace],
  );

  const submitDisabled = useMemo(
    () => !dateTime || !location || !description,
    [dateTime, location, description],
  );

  const completeView = (
    <div className={styles.submission}>
      <div className={styles.page}>
        <img
          src={workspaces.company?.logoUrl || blueLogo}
          alt="OPAS Mobile Logo"
          className={styles.contentLogo}
        />
        <Text weight="semiBold" size="lg" textAlign="center">
          Thank you for your observation!
        </Text>
        <Text weight="semiBold" textAlign="center">
          Every detail helps us build a safer future.
        </Text>
        <Text weight="semiBold" textAlign="center" size="sm">
          See something else? Hit the button below to report 🚀.
        </Text>
        <Button
          value="Add Observation"
          icon="add"
          cin="new"
          onClick={() => setSubmitted(false)}
        />
      </div>
    </div>
  );

  return loading ? (
    <CustomSpinner text="Loading..." />
  ) : !!workspaces?.length && !!accessKey ? (
    <div className={styles.container}>
      <div className={styles.graphicContainer}>
        <div className={styles.graphicChild}>
          <img alt="Opas logo" src={logo} />
          <p className={styles.graphicTitle}>{t(keys.utilities.LEFT_HEADER)}</p>
          <img
            alt="background graphic"
            className={styles.backgroundGraphic}
            src={background}
          />
        </div>
      </div>
      <div className={styles.contentContainer}>
        {submitted ? (
          completeView
        ) : (
          <div className={styles.page}>
            <div className={styles.logoContainer}>
              <img
                src={
                  workspace?.logoUrl || workspaces.company?.logoUrl || blueLogo
                }
                alt="OPAS Mobile Logo"
                className={styles.contentLogo}
              />
            </div>
            <Toast {...toastProps} onHide={() => hideToast()} />
            <div className={styles.titleContainer}>
              <Text noMargin size="lg" weight="bold" textAlign="center">
                {`${t(keys.observations.MAKE_AN_OBSERVATION)}`}
              </Text>
            </div>
            <div className={styles.topButtons}>
              <Button
                value={t(keys.observations.POSITIVE)}
                icon="mood"
                className={styles.button}
                outlined={negative}
                variant={negative ? 'secondary' : 'success'}
                onClick={() => update({ negative: false, type: null })}
                testId="observation"
              />
              <Button
                value={t(keys.observations.ACTION_REQUIRED)}
                icon="flag"
                variant={!negative ? 'secondary' : 'danger'}
                className={styles.button}
                outlined={!negative}
                onClick={() => update({ negative: true, type: null })}
                testId="observation"
              />
            </div>
            <br />
            <div className={styles.scroll}>
              <Text noMargin weight="semiBold">
                {t(keys.openLink.REPORTER_TEXT)}
              </Text>
              <Form.Control
                placeholder="Anonymous"
                value={reporter}
                onChange={({ target: { value } }) => setReporter(value)}
              />
              <Text noMargin weight="semiBold">
                {t(keys.observations.SELECT_DATE_AND_TIME)}
                <span className={styles.red}>*</span>
              </Text>
              <input
                type="datetime-local"
                onChange={(e) => {
                  const dateTime = e.target.value;
                  update({ dateTime });
                }}
                className={styles.dateSelector}
                value={dateTime}
                data-cy="observation-date-time"
              />
              <Text noMargin weight="semiBold">
                {t(keys.action.ENTER, { variable: t(keys.common.LOCATION) })}
                <span className={styles.red}>*</span>
              </Text>
              {!!workspace?.locations ? (
                <DropdownText
                  placeholder={'Location'}
                  selected={location}
                  items={workspace.locations.split('|')}
                  onChange={(location) => update({ location })}
                  onRemove={() => update({ location: null })}
                />
              ) : (
                <Form.Control
                  value={location || undefined}
                  onChange={(e) => update({ location: e.target.value })}
                  data-cy="observation-location"
                />
              )}
              <Text noMargin weight="semiBold">
                {t(keys.action.ENTER, {
                  variable: t(keys.observations.BRIEF_DESCRIPTION),
                })}
                <span className={styles.red}>*</span>
              </Text>
              <Form.Control
                as="textarea"
                onChange={(e) => update({ description: e.target.value })}
                value={description}
                data-cy="observation-description"
              />
              {questions?.map(
                ({
                  title,
                  description,
                  inputType,
                  required,
                  additionalData,
                  id,
                }) => {
                  const dateFormat = dateOptions[inputType];
                  const input = () => {
                    switch (inputType) {
                      case inputTypes.textMulti:
                        return (
                          <Form.Control
                            key={id}
                            as={'textarea'}
                            onChange={(e) =>
                              handleInputChange(
                                id,
                                e.target.value,
                                title,
                                description,
                              )
                            }
                            data-cy={`observation-question-${id}`}
                          />
                        );
                      case inputTypes.select:
                        return (
                          <div data-cy={`observation-question-${id}`}>
                            {additionalData?.split('|').map((a) => (
                              <Form.Check
                                key={a}
                                type="radio"
                                label={a}
                                inline
                                name={`singleselect-${id}`}
                                value={a}
                                onChange={(e) => {
                                  handleInputChange(
                                    id,
                                    e.target.value,
                                    title,
                                    description,
                                  );
                                }}
                              />
                            ))}
                          </div>
                        );
                      case inputTypes.multiSelect:
                        const values = questions?.find(
                          (q) => q.id === id,
                        )?.value;
                        return (
                          <MultiSelect
                            key={id}
                            title={' '}
                            inline
                            name={`multiselect-${id}`}
                            values={values || []}
                            options={additionalData?.split('|') || []}
                            onChange={(newValue) => {
                              handleInputChange(
                                id,
                                newValue,
                                title,
                                description,
                              );
                            }}
                            data-cy={`observation-question-${id}`}
                          />
                        );
                      case inputTypes.time:
                      case inputTypes.date:
                      case inputTypes.dateTime:
                        const type = () => {
                          switch (inputType) {
                            case inputTypes.time:
                              return 'time';
                            case inputTypes.dateTime:
                              return 'datetime-local';
                            default:
                              return 'date';
                          }
                        };
                        return (
                          <div key={id} data-cy={`observation-question-${id}`}>
                            <input
                              label={`${t(keys.action.SELECT_VARIABLE, {
                                variable: t(keys.common.DATE),
                              })} ${
                                inputType === inputTypes.date
                                  ? t(keys.templates.TIME)
                                  : ''
                              }`}
                              type={type()}
                              placeholder="Select Date"
                              className={styles.dateSelector}
                              id={`datetime-${id}`}
                              name={`datetime-${id}`}
                              onChange={(e) => {
                                let value = e.target.value;
                                if (inputType !== inputTypes.time)
                                  value = moment(e.target.value).format(
                                    dateFormat.format,
                                  );
                                handleInputChange(
                                  id,
                                  value,
                                  title,
                                  description,
                                );
                              }}
                              data-cy={`observation-question-${id}`}
                            ></input>
                          </div>
                        );
                      default:
                        return (
                          <Form.Control
                            key={id}
                            onChange={(e) =>
                              handleInputChange(
                                id,
                                e.target.value,
                                title,
                                description,
                              )
                            }
                            data-cy={`observation-question-${id}`}
                          />
                        );
                    }
                  };
                  return (
                    <div key={id} className={styles.templateQuestions}>
                      <div>
                        <Text noMargin weight="semiBold" size="sm">
                          {title} {required ? '*' : ''}
                        </Text>
                        <Text noMargin size="sm" color="secondary">
                          {description}
                        </Text>
                      </div>
                      {input()}
                    </div>
                  );
                },
              )}
            </div>
            <br />
            <Button
              disabled={submitDisabled}
              align={'right'}
              value={t(keys.action.SUBMIT)}
              icon={'save'}
              onClick={() => {
                const formatAnswers = answers?.map((q) => {
                  if (!q.value) {
                    q.value = t(keys.assessments.NO_ANSWER_PROVIDED);
                  }
                  return q;
                });
                const items = JSON.stringify(formatAnswers);
                submitObservation({
                  variables: {
                    ...observationState,
                    items,
                    description: reporter
                      ? `${reporter} - ${description}`
                      : description,
                    templateId: workspace?.observationTemplate?.id,
                    anonymous: true,
                    negative: !!negative,
                    workspaceId: parseInt(workspaceId),
                  },
                })
                  .then(() => {
                    setSubmitted(true);
                    setObservationState({});
                    setReporter(null);
                    setTimeout(() => {
                      setSubmitted(false);
                    }, 30000);
                  })
                  .catch((e) =>
                    showToast({
                      title: 'Error',
                      variant: toastVariant?.warning,
                      message: `There is a probelm with the link, contact an Admin for help`,
                    }),
                  );
              }}
            />
          </div>
        )}
      </div>
    </div>
  ) : (
    <div>
      <Text noMargin size="sm">
        No Key
      </Text>
      <DeletedReportPage />
    </div>
  );
}
